import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import RateAbbonamento from '../../components/admin/RateAbbonamento';
import StampaTesserino from '../../components/admin/StampaTesserino';
import api from '../../services/api';
import {
  ArrowLeft, Edit, Trash2, FileText, User, Calendar, CreditCard,
  Bus, CheckCircle, XCircle, AlertCircle, Download, QrCode, Shield, Plus, X, Printer, School
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

const AbbonamentoDettaglio = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [abbonamento, setAbbonamento] = useState(null);
  const [titolare, setTitolare] = useState(null);
  const [linea, setLinea] = useState(null);
  const [pagamenti, setPagamenti] = useState([]);
  const [controlli, setControlli] = useState([]);
  const [qrToken, setQrToken] = useState('');
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [override, setOverride] = useState([]);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [showTesserino, setShowTesserino] = useState(false);
  const [nuovoOverride, setNuovoOverride] = useState({
    data_scadenza: '',
    motivo: ''
  });

  useEffect(() => {
    caricaDatiAbbonamento();
  }, [id]);

  const caricaDatiAbbonamento = async () => {
    try {
      setLoading(true);
      
      // Carica abbonamento
      const abbRes = await api.get(`/abbonamenti/abbonamenti/${id}/`);
      const abbData = abbRes.data;
      setAbbonamento(abbData);

      // Genera/Recupera QR token se abbonamento è valido
      if ((abbData.stato_effettivo || abbData.stato) === 'ATTIVO' && abbData.qr_token) {
        setQrToken(abbData.qr_token);
      }

      // Carica override
      const overrideRes = await api.get(`/abbonamenti/override/?abbonamento=${id}`);
      setOverride(overrideRes.data.results || overrideRes.data);

      // Il cliente è il titolare dell'abbonamento: il campo "beneficiario"
      // non esiste sul modello, quindi la scheda restava sempre vuota
      if (abbData.titolare) {
        const titRes = await api.get(`/anagrafiche/titolari/${abbData.titolare}/`);
        setTitolare(titRes.data);
      }

      // Carica linea
      if (abbData.linea) {
        const lineaRes = await api.get(`/trasporti/linee/${abbData.linea}/`);
        setLinea(lineaRes.data);
      }

      // Carica pagamenti
      const pagRes = await api.get(`/pagamenti/pagamenti/?abbonamento=${id}`);
      setPagamenti(pagRes.data.results || pagRes.data);

      // Carica controlli
      const contrRes = await api.get(`/controlli/eventi/?abbonamento=${id}`);
      setControlli(contrRes.data.results || contrRes.data);

    } catch (error) {
      console.error('Errore caricamento abbonamento:', error);
      alert('Errore nel caricamento dei dati dell\'abbonamento');
    } finally {
      setLoading(false);
    }
  };

  const handleRigeneraQR = async () => {
    try {
      const response = await api.post(`/abbonamenti/abbonamenti/${id}/genera_token/`);
      setQrToken(response.data.qr_token);
      alert('QR Code rigenerato con successo!');
    } catch (error) {
      console.error('Errore rigenerazione QR:', error);
      alert('Errore nella rigenerazione del QR Code');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Sei sicuro di voler eliminare questo abbonamento?')) {
      return;
    }
    
    try {
      await api.delete(`/abbonamenti/abbonamenti/${id}/`);
      alert('Abbonamento eliminato con successo!');
      navigate('/admin/abbonamenti');
    } catch (error) {
      console.error('Errore eliminazione abbonamento:', error);
      alert('Errore nell\'eliminazione dell\'abbonamento');
    }
  };

  const handleCreaOverride = async () => {
    if (!nuovoOverride.data_scadenza || !nuovoOverride.motivo) {
      alert('Compila tutti i campi obbligatori');
      return;
    }

    try {
      await api.post('/abbonamenti/override/', {
        abbonamento: parseInt(id),
        data_scadenza: new Date(nuovoOverride.data_scadenza).toISOString(),
        motivo: nuovoOverride.motivo,
        attivo: true
      });
      
      alert('Override creato con successo!');
      setShowOverrideModal(false);
      setNuovoOverride({ data_scadenza: '', motivo: '' });
      caricaDatiAbbonamento();
    } catch (error) {
      console.error('Errore creazione override:', error);
      alert('Errore nella creazione dell\'override');
    }
  };

  const handleRevocaOverride = async (overrideId) => {
    if (!window.confirm('Sei sicuro di voler revocare questo override?')) {
      return;
    }

    try {
      await api.patch(`/abbonamenti/override/${overrideId}/`, {
        attivo: false
      });
      
      alert('Override revocato con successo!');
      caricaDatiAbbonamento();
    } catch (error) {
      console.error('Errore revoca override:', error);
      alert('Errore nella revoca dell\'override');
    }
  };

  const overrideAttivo = override.find(o => o.attivo && o.is_valido);

  // scuole_servite è un JSON: stampandolo così com'è si vedeva la stringa
  // grezza con virgolette e codici meccanografici
  const scuoleDellaLinea = (() => {
    const grezzo = linea?.scuole_servite;
    if (!grezzo) return [];
    try {
      const dati = typeof grezzo === 'string' ? JSON.parse(grezzo) : grezzo;
      if (!Array.isArray(dati)) return [];
      return dati.map((s) => ({
        nome: s.denominazione || s.denominazione_scuola || s.nome || '—',
        comune: s.comune || '',
      }));
    } catch {
      // Testo libero inserito a mano: si mostra così com'è
      return [{ nome: String(grezzo), comune: '' }];
    }
  })();

  const getStatoBadgeClass = (stato) => {
    switch (stato) {
      case 'ATTIVO': return 'bg-green-100 text-green-800';
      case 'SCADUTO': return 'bg-red-100 text-red-800';
      case 'IN_ATTESA_PAGAMENTO': return 'bg-orange-100 text-orange-800';
      case 'SOSPESO': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatoPagamentoBadge = (stato) => {
    switch (stato) {
      case 'COMPLETATO': return 'bg-green-100 text-green-800';
      case 'IN_ATTESA': return 'bg-orange-100 text-orange-800';
      case 'RIFIUTATO': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // esito è un booleano: confrontarlo con 'VALIDO' non dava mai riscontro,
  // quindi l'icona restava sempre quella generica e il conteggio a zero
  const getEsitoControlloIcon = (esito) =>
    esito
      ? <CheckCircle className="w-4 h-4 text-green-600" />
      : <XCircle className="w-4 h-4 text-red-600" />;

  // Il motivo arriva in maiuscolo con trattini bassi
  const descriviMotivo = (motivo) =>
    motivo ? motivo.replace(/_/g, ' ').toLowerCase() : '';

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  if (!abbonamento) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <AlertCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Abbonamento non trovato</h2>
          <button
            onClick={() => navigate('/admin/abbonamenti')}
            className="text-blue-600 hover:text-blue-700"
          >
            Torna alla lista abbonamenti
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6">
        {/* Header */}
        <div className="mb-5">
          <button
            onClick={() => navigate('/admin/abbonamenti')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Torna agli abbonamenti
          </button>
          
          <div className="flex flex-wrap justify-between items-start gap-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Abbonamento {abbonamento.codice}
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">
                {titolare
                  ? `${titolare.nome} ${titolare.cognome}`
                  : abbonamento.titolare_nome || 'Cliente non disponibile'}
                {abbonamento.tratta && ` · ${abbonamento.tratta}`}
              </p>
            </div>
            <div className="flex gap-3 items-center">
              {overrideAttivo && (
                <span className="px-4 py-2 text-sm rounded-full font-semibold bg-purple-100 text-purple-800 border-2 border-purple-300 flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Override Attivo
                </span>
              )}
              {/* Lo stato mostrato è quello calcolato: il campo memorizzato
                  può essere indietro di un mese sugli abbonamenti scolastici */}
              <div className="flex flex-col items-end">
                <span className={`px-4 py-2 text-sm rounded-full font-semibold ${getStatoBadgeClass(abbonamento.stato_effettivo || abbonamento.stato)}`}>
                  {abbonamento.badge_info?.icona} {abbonamento.badge_info?.stato
                    || (abbonamento.stato_effettivo || abbonamento.stato || '').replace(/_/g, ' ')}
                </span>
                {abbonamento.badge_info?.messaggio && (
                  <span className="text-xs text-gray-500 mt-1">{abbonamento.badge_info.messaggio}</span>
                )}
              </div>
              <button
                onClick={() => setShowTesserino(true)}
                className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition"
              >
                <Printer className="w-4 h-4" />
                Stampa tesserino
              </button>
              <button
                onClick={handleDelete}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
              >
                <Trash2 className="w-4 h-4" />
                Elimina
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Colonna sinistra */}
          <div className="lg:col-span-2 space-y-6">
            {/* Dettagli Abbonamento */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <FileText className="w-4 h-4 text-blue-600" />
                Dettagli Abbonamento
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Codice</p>
                  <p className="font-semibold text-gray-900">{abbonamento.codice}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Tipo</p>
                  <p className="font-semibold text-gray-900">{abbonamento.tipo_abbonamento || 'Standard'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Data Inizio</p>
                  <p className="font-semibold text-gray-900">
                    {new Date(abbonamento.data_inizio).toLocaleDateString('it-IT')}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Data Fine</p>
                  <p className="font-semibold text-gray-900">
                    {new Date(abbonamento.data_fine).toLocaleDateString('it-IT')}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Importo</p>
                  <p className="font-semibold text-gray-900">€ {parseFloat(abbonamento.importo).toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Stato Pagamento</p>
                  <span className={`inline-block px-2 py-1 text-xs rounded-full font-semibold ${
                    abbonamento.pagato ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {abbonamento.pagato ? 'Pagato' : 'Non Pagato'}
                  </span>
                </div>
              </div>
            </div>

            {/* Cliente */}
            {titolare && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <User className="w-4 h-4 text-green-600" />
                  Cliente
                </h2>
                <div className="border-l-4 border-blue-500 pl-4 space-y-0.5">
                  <button
                    onClick={() => navigate(`/admin/clienti/${titolare.id}`)}
                    className="font-semibold text-gray-900 hover:text-[#1b94ce] transition text-left"
                  >
                    {titolare.nome} {titolare.cognome}
                  </button>
                  {titolare.codice_fiscale && (
                    <p className="text-sm text-gray-600">CF: {titolare.codice_fiscale}</p>
                  )}
                  {titolare.email && <p className="text-sm text-gray-600">{titolare.email}</p>}
                  {titolare.telefono && <p className="text-sm text-gray-600">{titolare.telefono}</p>}
                  {titolare.scuola && <p className="text-sm text-gray-600">Scuola: {titolare.scuola}</p>}
                </div>
              </div>
            )}

            {/* Linea */}
            {linea && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Bus className="w-4 h-4 text-indigo-600" />
                  Linea di Trasporto
                </h2>
                <div className="space-y-2">
                  <p className="font-semibold text-gray-900">{linea.codice} - {linea.nome}</p>
                  {linea.descrizione && (
                    <p className="text-sm text-gray-600">{linea.descrizione}</p>
                  )}
                  {scuoleDellaLinea.length > 0 && (
                    <div className="pt-2">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                        Scuole servite ({scuoleDellaLinea.length})
                      </p>
                      <ul className="space-y-1">
                        {scuoleDellaLinea.map((s, i) => (
                          <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                            <School className="w-3.5 h-3.5 text-gray-400 mt-0.5 shrink-0" />
                            <span>
                              {s.nome}
                              {s.comune && <span className="text-gray-500"> · {s.comune}</span>}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Mensilità (abbonamenti scolastici) */}
            {abbonamento.rate?.length > 0 && (
              <RateAbbonamento
                abbonamento={abbonamento}
                onAggiornato={caricaDatiAbbonamento}
              />
            )}

            {/* Pagamenti: per gli abbonamenti scolastici i movimenti nascono
                dalle mensilità, quindi lo storico si mostra solo se serve */}
            <div className={`bg-white rounded-lg shadow-sm p-6 ${
              abbonamento.rate?.length > 0 && pagamenti.length === 0 ? 'hidden' : ''
            }`}>
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-green-600" />
                {abbonamento.rate?.length > 0 ? 'Movimenti contabili' : 'Pagamenti'} ({pagamenti.length})
              </h2>
              {pagamenti.length === 0 ? (
                <p className="text-gray-500 text-center py-4">Nessun pagamento registrato</p>
              ) : (
                <div className="space-y-3">
                  {pagamenti.map((pag) => (
                    <div key={pag.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-semibold text-gray-900">€ {parseFloat(pag.importo).toFixed(2)}</p>
                          <p className="text-sm text-gray-600">
                            {new Date(pag.data_pagamento).toLocaleDateString('it-IT')}
                          </p>
                        </div>
                        <span className={`px-2 py-1 text-xs rounded-full font-semibold ${getStatoPagamentoBadge(pag.stato)}`}>
                          {pag.stato}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">Metodo: {pag.metodo_pagamento}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Controlli */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-blue-600" />
                Controlli Recenti ({controlli.slice(0, 10).length})
              </h2>
              {controlli.length === 0 ? (
                <p className="text-gray-500 text-center py-4">Nessun controllo effettuato</p>
              ) : (
                <div className="space-y-2">
                  {controlli.slice(0, 10).map((ctrl) => (
                    <div key={ctrl.id} className="flex items-center justify-between border-b border-gray-100 pb-2">
                      <div className="flex items-center gap-3">
                        {getEsitoControlloIcon(ctrl.esito)}
                        <div>
                          <p className="text-sm font-semibold text-gray-900">
                            {new Date(ctrl.timestamp || ctrl.data_controllo).toLocaleString('it-IT')}
                          </p>
                          <p className="text-xs text-gray-600">
                            {ctrl.esito ? 'Accettato' : 'Respinto'}
                            {!ctrl.esito && ctrl.motivo && ` · ${descriviMotivo(ctrl.motivo)}`}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Override Abbonamento */}
            <div className="bg-white rounded-lg shadow-sm p-6 border-2 border-purple-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-purple-600" />
                  Override Abbonamento
                </h2>
                <button
                  onClick={() => setShowOverrideModal(true)}
                  className="flex items-center gap-2 px-3 py-1.5 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition"
                >
                  <Plus className="w-4 h-4" />
                  Nuovo Override
                </button>
              </div>
              
              <p className="text-sm text-gray-600 mb-4">
                Gli override permettono di autorizzare temporaneamente abbonamenti scaduti o sospesi per situazioni eccezionali.
              </p>

              {override.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                  <Shield className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                  <p className="text-gray-500 text-sm">Nessun override registrato</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {override.map((ovr) => (
                    <div
                      key={ovr.id}
                      className={`border-2 rounded-lg p-4 ${
                        ovr.attivo && ovr.is_valido
                          ? 'border-purple-300 bg-purple-50'
                          : 'border-gray-200 bg-gray-50'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span
                              className={`px-2 py-1 text-xs rounded-full font-semibold ${
                                ovr.attivo && ovr.is_valido
                                  ? 'bg-purple-200 text-purple-900'
                                  : 'bg-gray-200 text-gray-700'
                              }`}
                            >
                              {ovr.attivo && ovr.is_valido ? 'ATTIVO' : ovr.attivo ? 'SCADUTO' : 'REVOCATO'}
                            </span>
                            <span className="text-xs text-gray-500">
                              da {ovr.autorizzato_da_nome || 'Admin'}
                            </span>
                          </div>
                          <p className="text-sm font-medium text-gray-900 mb-1">{ovr.motivo}</p>
                          <div className="flex gap-4 text-xs text-gray-600">
                            <span>
                              Creato: {new Date(ovr.data_creazione).toLocaleDateString('it-IT')}
                            </span>
                            <span>
                              Scade: {new Date(ovr.data_scadenza).toLocaleDateString('it-IT')}
                            </span>
                          </div>
                        </div>
                        {ovr.attivo && (
                          <button
                            onClick={() => handleRevocaOverride(ovr.id)}
                            className="ml-2 p-1.5 text-red-600 hover:bg-red-50 rounded transition"
                            title="Revoca Override"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Colonna destra */}
          <div className="space-y-6">
            {/* QR Code */}
            {(abbonamento.stato_effettivo || abbonamento.stato) === 'ATTIVO' && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <QrCode className="w-4 h-4 text-blue-600" />
                  QR Code
                </h3>
                <div className="flex flex-col items-center">
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    {/* Si mostra il codice abbonamento, non il token JWT:
                        quello scade dopo 5 minuti e il QR a video diventerebbe
                        inservibile. Il server accetta entrambi e verifica
                        stato e gruppo al momento della scansione. */}
                    <QRCodeSVG value={abbonamento.codice} size={200} level="M" />
                  </div>
                  <button
                    onClick={handleRigeneraQR}
                    className="mt-4 text-sm text-blue-600 hover:text-blue-700"
                  >
                    Rigenera QR Code
                  </button>
                </div>
              </div>
            )}

            {/* Stats */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold mb-4 text-gray-900">Statistiche</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span>Giorni Rimanenti</span>
                  <span className="text-2xl font-bold text-gray-900">
                    {Math.max(0, Math.ceil((new Date(abbonamento.data_fine) - new Date()) / (1000 * 60 * 60 * 24)))}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Controlli Totali</span>
                  <span className="text-2xl font-bold text-gray-900">{controlli.length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Controlli Accettati</span>
                  <span className="text-2xl font-bold text-gray-900">
                    {controlli.filter(c => c.esito).length}
                  </span>
                </div>
              </div>
            </div>

            {/* Info Sistema */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-600" />
                Info Sistema
              </h3>
              <div className="space-y-2 text-sm">
                <div>
                  <p className="text-gray-500">Creato il</p>
                  <p className="font-semibold text-gray-900">
                    {new Date(abbonamento.data_creazione).toLocaleDateString('it-IT')}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Ultima modifica</p>
                  <p className="font-semibold text-gray-900">
                    {new Date(abbonamento.data_modifica).toLocaleDateString('it-IT')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Nuovo Override */}
        {showOverrideModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-purple-600" />
                  Crea Nuovo Override
                </h3>
                <button
                  onClick={() => {
                    setShowOverrideModal(false);
                    setNuovoOverride({ data_scadenza: '', motivo: '' });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                <p className="text-sm text-purple-900">
                  <strong>Attenzione:</strong> Gli override autorizzano temporaneamente l'uso di abbonamenti scaduti o sospesi. 
                  Utilizza questa funzione solo per situazioni eccezionali e documenta accuratamente il motivo.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Data Scadenza Override <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    value={nuovoOverride.data_scadenza}
                    onChange={(e) => setNuovoOverride({ ...nuovoOverride, data_scadenza: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    min={new Date().toISOString().slice(0, 16)}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Data e ora fino a cui l'override sarà valido
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Motivo dell'Override <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={nuovoOverride.motivo}
                    onChange={(e) => setNuovoOverride({ ...nuovoOverride, motivo: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    rows="4"
                    placeholder="Es: Ritardo nel rinnovo dovuto a problemi bancari del cliente"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Descrivi dettagliatamente il motivo dell'autorizzazione eccezionale
                  </p>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowOverrideModal(false);
                    setNuovoOverride({ data_scadenza: '', motivo: '' });
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                >
                  Annulla
                </button>
                <button
                  onClick={handleCreaOverride}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition font-semibold"
                >
                  Crea Override
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {showTesserino && (
        <StampaTesserino
          abbonamento={abbonamento}
          onClose={() => setShowTesserino(false)}
        />
      )}
    </AdminLayout>
  );
};

export default AbbonamentoDettaglio;

